import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  RefreshCw,
  Bot,
  Globe,
  Link2,
  Sparkles,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  TrendingUp,
  Activity,
  Zap,
  ChevronDown,
  ChevronUp,
  FileText,
  AlertTriangle,
  Search,
  Timer,
  ExternalLink,
  Hash,
  Database,
  Plus,
  SkipForward,
  ShieldCheck,
  ShieldX,
  ShieldAlert,
  Building2,
  MapPin,
  DollarSign
} from "lucide-react";
import { toast } from "sonner";

const AGENT_TYPES = {
  scraper: { label: "Scraper Agent", icon: Globe, color: "#2a9d8f" },
  link_verifier: { label: "Link Verifier", icon: Link2, color: "#e9c46a" },
  lead_scorer: { label: "Lead Scorer", icon: TrendingUp, color: "#f4a261" },
  lead_enricher: { label: "Lead Enricher", icon: Sparkles, color: "#40c9ba" },
  lead_aging: { label: "Lead Aging", icon: Clock, color: "#e76f51" }
};

const STATUS_CONFIG = {
  running: { label: "Running", icon: Loader2, color: "#e9c46a", bgColor: "rgba(233, 196, 106, 0.2)" },
  completed: { label: "Completed", icon: CheckCircle, color: "#2a9d8f", bgColor: "rgba(42, 157, 143, 0.2)" },
  failed: { label: "Failed", icon: XCircle, color: "#e76f51", bgColor: "rgba(231, 111, 81, 0.2)" }
};

export default function AgentActivity() {
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expandedIds, setExpandedIds] = useState(new Set());

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [activitiesRes, statsRes] = await Promise.all([
        api.get('/agent-activity?limit=100'),
        api.get('/agent-activity/stats')
      ]);
      setActivities(activitiesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching agent activity:", error);
      toast.error("Failed to load agent activity");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatDuration = (seconds) => {
    if (!seconds) return "-";
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return "-";
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  const filteredActivities = filter === "all" 
    ? activities 
    : activities.filter(a => a.agent_type === filter);

  const getResultsSummary = (activity) => {
    const results = activity.results || {};
    
    if (activity.agent_type === "scraper") {
      return (
        <div className="flex gap-3 text-xs font-mono">
          <span className="text-theme-primary">+{results.leads_added || 0} added</span>
          <span className="text-theme-muted">{results.leads_found || 0} found</span>
          {results.duplicates_skipped > 0 && (
            <span className="text-theme-secondary">{results.duplicates_skipped} dupes</span>
          )}
        </div>
      );
    }
    
    if (activity.agent_type === "link_verifier") {
      return (
        <div className="flex gap-3 text-xs font-mono">
          <span className="text-theme-primary">{results.valid_count || 0} valid</span>
          <span className="text-theme-secondary">{results.login_wall_count || 0} login</span>
          <span className="text-theme-danger">{results.invalid_count || 0} invalid</span>
        </div>
      );
    }
    
    if (activity.agent_type === "lead_aging") {
      return (
        <div className="flex gap-3 text-xs font-mono">
          <span className="text-theme-secondary">{results.moved_to_stale || 0} → stale</span>
          <span className="text-[#6c757d]">{results.moved_to_archived || 0} → archived</span>
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="space-y-6" data-testid="agent-activity-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div
            className="w-14 h-12 rounded-xl bg-gradient-to-br from-[#e9c46a] to-[#f4a261] flex items-center justify-center"
            style={{ boxShadow: "4px 4px 0 rgba(139, 69, 19, 0.4)" }}
          >
            <Bot className="w-7 h-7 text-theme-dark" />
          </div>
          <div>
            <h1
              className="text-3xl font-bold text-theme-light"
              style={{ textShadow: "2px 2px 4px rgba(0,0,0,0.3)" }}
            >
              Agent Activity
            </h1>
            <p className="text-theme-muted text-sm font-mono">
              AI AGENT WORK HISTORY & STATS
            </p>
          </div>
        </div>
        <Button
          onClick={fetchData}
          disabled={loading}
          className="bg-gradient-to-r from-[#2a9d8f] to-[#1a6b62] hover:from-[#40c9ba] hover:to-[#2a9d8f] text-theme-light border-2 border-[#40c9ba]/50 font-bold"
          style={{ boxShadow: "3px 3px 0 rgba(42, 157, 143, 0.4)" }}
          data-testid="refresh-activity-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          REFRESH
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div
            className="p-4 rounded-xl bg-theme-darker/80 border-2 border-theme-primary/30"
            style={{ boxShadow: "3px 3px 0 rgba(38, 70, 83, 0.5)" }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-5 h-5 text-theme-primary" />
              <span className="text-xs text-theme-muted font-mono">TODAY'S RUNS</span>
            </div>
            <p className="text-3xl font-bold text-theme-light font-mono">
              {stats.today?.total_runs || 0}
            </p>
          </div>
          
          <div
            className="p-4 rounded-xl bg-theme-darker/80 border-2 border-theme-secondary/30"
            style={{ boxShadow: "3px 3px 0 rgba(38, 70, 83, 0.5)" }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-5 h-5 text-theme-secondary" />
              <span className="text-xs text-theme-muted font-mono">LEADS SCRAPED</span>
            </div>
            <p className="text-3xl font-bold text-theme-secondary font-mono">
              {stats.today?.leads_scraped || 0}
            </p>
          </div>
          
          <div
            className="p-4 rounded-xl bg-theme-darker/80 border-2 border-[#40c9ba]/30"
            style={{ boxShadow: "3px 3px 0 rgba(38, 70, 83, 0.5)" }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Link2 className="w-5 h-5 text-theme-primary-light" />
              <span className="text-xs text-theme-muted font-mono">LINKS VERIFIED</span>
            </div>
            <p className="text-3xl font-bold text-theme-primary-light font-mono">
              {stats.today?.links_verified || 0}
            </p>
          </div>
          
          <div
            className="p-4 rounded-xl bg-theme-darker/80 border-2 border-theme-accent/30"
            style={{ boxShadow: "3px 3px 0 rgba(38, 70, 83, 0.5)" }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-5 h-5 text-theme-accent" />
              <span className="text-xs text-theme-muted font-mono">SUCCESS RATE</span>
            </div>
            <p className="text-3xl font-bold text-theme-accent font-mono">
              {stats.by_agent_type?.scraper?.success_rate || 100}%
            </p>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <Button
          variant={filter === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("all")}
          className={filter === "all" 
            ? "bg-theme-primary text-theme-light border-[#40c9ba]" 
            : "border-theme-primary/40 text-theme-muted hover:text-theme-light hover:bg-theme-primary/20"
          }
        >
          All Agents
        </Button>
        {Object.entries(AGENT_TYPES).map(([type, config]) => (
          <Button
            key={type}
            variant={filter === type ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(type)}
            className={filter === type 
              ? "text-theme-light" 
              : "border-theme-primary/40 text-theme-muted hover:text-theme-light hover:bg-theme-primary/20"
            }
            style={filter === type ? { backgroundColor: config.color } : {}}
          >
            <config.icon className="w-4 h-4 mr-1" />
            {config.label}
          </Button>
        ))}
      </div>

      {/* Activity List */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          background: "linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))",
          border: "3px solid rgba(233, 196, 106, 0.3)",
          boxShadow: "6px 6px 0 rgba(139, 69, 19, 0.2)"
        }}
      >
        <div className="p-4 border-b-2 border-theme-secondary/30 bg-theme-darker/50">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-theme-light">Recent Activity</h2>
            <Badge className="bg-theme-primary/20 text-theme-primary-light border border-theme-primary/40 font-mono">
              {filteredActivities.length} entries
            </Badge>
          </div>
        </div>

        <ScrollArea className="h-[500px]">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-8 h-8 text-theme-primary animate-spin" />
            </div>
          ) : filteredActivities.length === 0 ? (
            <div className="p-12 text-center">
              <Bot className="w-16 h-16 mx-auto mb-4 text-theme-primary/50" />
              <p className="text-theme-muted font-mono">NO AGENT ACTIVITY YET</p>
              <p className="text-sm text-theme-muted/70 mt-2">
                Run a scrape or verify links to see activity here
              </p>
            </div>
          ) : (
            <div className="divide-y divide-[#2a9d8f]/20">
              {filteredActivities.map((activity) => {
                const agentConfig = AGENT_TYPES[activity.agent_type] || AGENT_TYPES.scraper;
                const statusConfig = STATUS_CONFIG[activity.status] || STATUS_CONFIG.running;
                const AgentIcon = agentConfig.icon;
                const StatusIcon = statusConfig.icon;
                const isExpanded = expandedIds.has(activity.id);

                return (
                  <Collapsible
                    key={activity.id}
                    open={isExpanded}
                    onOpenChange={() => toggleExpand(activity.id)}
                  >
                    <div
                      className="hover:bg-theme-primary/10 transition-colors"
                      data-testid={`activity-${activity.id}`}
                    >
                      <CollapsibleTrigger asChild>
                        <div className="p-4 cursor-pointer">
                          <div className="flex items-start gap-4">
                            {/* Agent Icon */}
                            <div
                              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: `${agentConfig.color}20` }}
                            >
                              <AgentIcon className="w-5 h-5" style={{ color: agentConfig.color }} />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold text-theme-light">
                                  {agentConfig.label}
                                </span>
                                <Badge
                                  className="text-xs font-mono"
                                  style={{
                                    backgroundColor: statusConfig.bgColor,
                                    color: statusConfig.color,
                                    border: `1px solid ${statusConfig.color}40`
                                  }}
                                >
                                  <StatusIcon className={`w-3 h-3 mr-1 ${activity.status === 'running' ? 'animate-spin' : ''}`} />
                                  {statusConfig.label}
                                </Badge>
                              </div>

                              {/* Brief Details */}
                              {activity.details && (
                                <div className="text-xs text-theme-muted mb-2">
                                  {activity.details.sources && (
                                    <span>Sources: {activity.details.sources.join(", ")}</span>
                                  )}
                                  {activity.details.keywords && (
                                    <span className="ml-2">• Keywords: {activity.details.keywords.slice(0, 3).join(", ")}</span>
                                  )}
                                  {activity.details.max_leads && (
                                    <span>Max leads: {activity.details.max_leads}</span>
                                  )}
                                </div>
                              )}

                              {/* Results Summary */}
                              {activity.status === "completed" && getResultsSummary(activity)}
                              
                              {/* Error */}
                              {activity.error_message && (
                                <p className="text-xs text-theme-danger mt-1 font-mono">
                                  Error: {activity.error_message}
                                </p>
                              )}
                            </div>

                            {/* Time, Duration & Expand Button */}
                            <div className="text-right flex-shrink-0 flex items-center gap-3">
                              <div>
                                <p className="text-sm text-theme-light font-mono">
                                  {formatTime(activity.started_at)}
                                </p>
                                {activity.duration_seconds && (
                                  <p className="text-xs text-theme-muted font-mono">
                                    {formatDuration(activity.duration_seconds)}
                                  </p>
                                )}
                              </div>
                              <div
                                className="w-8 h-8 rounded-lg flex items-center justify-center bg-theme-primary/20 hover:bg-theme-primary/40 transition-colors"
                              >
                                {isExpanded ? (
                                  <ChevronUp className="w-4 h-4 text-theme-primary-light" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-theme-primary-light" />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      
                      {/* Expanded Details Panel */}
                      <CollapsibleContent>
                        <div className="px-4 pb-4 pt-0">
                          <div 
                            className="p-4 rounded-xl bg-[#0d1f24] border border-theme-primary/30"
                            data-testid={`activity-details-${activity.id}`}
                          >
                            {/* Details Section */}
                            {activity.details && Object.keys(activity.details).length > 0 && (
                              <div className="mb-4">
                                <div className="flex items-center gap-2 mb-3">
                                  <FileText className="w-4 h-4 text-theme-secondary" />
                                  <h4 className="text-sm font-semibold text-theme-secondary">Configuration</h4>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {activity.details.sources && (
                                    <div className="p-3 rounded-lg bg-theme-darker/50">
                                      <p className="text-xs text-theme-muted mb-1">Sources</p>
                                      <div className="flex flex-wrap gap-1">
                                        {activity.details.sources.map((source, idx) => (
                                          <Badge key={idx} className="text-xs bg-theme-primary/30 text-theme-primary-light border-0">
                                            <Globe className="w-3 h-3 mr-1" />
                                            {source}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {activity.details.keywords && (
                                    <div className="p-3 rounded-lg bg-theme-darker/50">
                                      <p className="text-xs text-theme-muted mb-1">Keywords</p>
                                      <div className="flex flex-wrap gap-1">
                                        {activity.details.keywords.map((keyword, idx) => (
                                          <Badge key={idx} className="text-xs bg-theme-secondary/20 text-theme-secondary border-0">
                                            <Search className="w-3 h-3 mr-1" />
                                            {keyword}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {activity.details.date_posted && (
                                    <div className="p-3 rounded-lg bg-theme-darker/50">
                                      <p className="text-xs text-theme-muted mb-1">Date Filter</p>
                                      <p className="text-sm text-theme-light font-mono">{activity.details.date_posted}</p>
                                    </div>
                                  )}
                                  {activity.details.max_leads && (
                                    <div className="p-3 rounded-lg bg-theme-darker/50">
                                      <p className="text-xs text-theme-muted mb-1">Max Leads</p>
                                      <p className="text-sm text-theme-light font-mono">{activity.details.max_leads}</p>
                                    </div>
                                  )}
                                  {activity.details.lead_ids && (
                                    <div className="p-3 rounded-lg bg-theme-darker/50 md:col-span-2">
                                      <p className="text-xs text-theme-muted mb-1">Lead IDs Processed</p>
                                      <p className="text-xs text-theme-light font-mono break-all">
                                        {activity.details.lead_ids.length > 5 
                                          ? `${activity.details.lead_ids.slice(0, 5).join(", ")}... (+${activity.details.lead_ids.length - 5} more)`
                                          : activity.details.lead_ids.join(", ") || "All pending leads"
                                        }
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Results Summary Section */}
                            {activity.results && Object.keys(activity.results).length > 0 && (
                              <div className="mb-4">
                                <div className="flex items-center gap-2 mb-3">
                                  <Database className="w-4 h-4 text-theme-primary" />
                                  <h4 className="text-sm font-semibold text-theme-primary">Results Summary</h4>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                  {Object.entries(activity.results)
                                    .filter(([key]) => !key.includes("details") && !key.includes("errors") || key === "errors_count")
                                    .map(([key, value]) => (
                                    <div key={key} className="p-3 rounded-lg bg-theme-darker/50 text-center">
                                      <p className="text-xs text-theme-muted mb-1 capitalize">
                                        {key.replace(/_/g, " ")}
                                      </p>
                                      <p className={`text-lg font-bold font-mono ${
                                        key.includes("added") || key.includes("valid") ? "text-theme-primary" :
                                        key.includes("error") || key.includes("invalid") ? "text-theme-danger" :
                                        key.includes("skip") || key.includes("dupe") || key.includes("login") ? "text-theme-secondary" :
                                        "text-theme-light"
                                      }`}>
                                        {typeof value === "number" ? value : String(value)}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Detailed Logs Section - Scraper */}
                            {activity.agent_type === "scraper" && activity.results && 
                             (activity.results.leads_added_details?.length > 0 || activity.results.leads_skipped_details?.length > 0) && (
                              <div className="mb-4">
                                <div className="flex items-center gap-2 mb-3">
                                  <FileText className="w-4 h-4 text-theme-primary-light" />
                                  <h4 className="text-sm font-semibold text-theme-primary-light">Detailed Logs</h4>
                                </div>
                                <Tabs defaultValue="added" className="w-full">
                                  <TabsList className="bg-theme-darker/50 border border-theme-primary/30">
                                    <TabsTrigger 
                                      value="added" 
                                      className="data-[state=active]:bg-theme-primary data-[state=active]:text-theme-light text-theme-muted"
                                    >
                                      <Plus className="w-3 h-3 mr-1" />
                                      Added ({activity.results.leads_added_details?.length || 0})
                                    </TabsTrigger>
                                    <TabsTrigger 
                                      value="skipped"
                                      className="data-[state=active]:bg-theme-secondary data-[state=active]:text-theme-dark text-theme-muted"
                                    >
                                      <SkipForward className="w-3 h-3 mr-1" />
                                      Skipped ({activity.results.leads_skipped_details?.length || 0})
                                    </TabsTrigger>
                                  </TabsList>
                                  
                                  <TabsContent value="added" className="mt-3">
                                    <div className="max-h-[300px] overflow-y-auto space-y-2 pr-2">
                                      {activity.results.leads_added_details?.length > 0 ? (
                                        activity.results.leads_added_details.map((lead, idx) => (
                                          <div 
                                            key={idx} 
                                            className="p-3 rounded-lg bg-theme-darker/70 border border-theme-primary/30 hover:border-theme-primary/60 transition-colors"
                                          >
                                            <div className="flex items-start justify-between gap-2">
                                              <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-theme-light truncate">{lead.title}</p>
                                                <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-theme-muted">
                                                  <span className="flex items-center gap-1">
                                                    <Building2 className="w-3 h-3" />
                                                    {lead.client}
                                                  </span>
                                                  {lead.location && (
                                                    <span className="flex items-center gap-1">
                                                      <MapPin className="w-3 h-3" />
                                                      {lead.location}
                                                    </span>
                                                  )}
                                                  {lead.rate && (
                                                    <span className="flex items-center gap-1">
                                                      <DollarSign className="w-3 h-3" />
                                                      {lead.rate}
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="flex items-center gap-2">
                                                {lead.score !== undefined && (
                                                  <Badge className="bg-theme-primary/20 text-theme-primary border-0 font-mono">
                                                    {lead.score}
                                                  </Badge>
                                                )}
                                                {lead.link && (
                                                  <a 
                                                    href={lead.link} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="text-theme-primary-light hover:text-theme-light transition-colors"
                                                  >
                                                    <ExternalLink className="w-4 h-4" />
                                                  </a>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        ))
                                      ) : (
                                        <p className="text-sm text-theme-muted text-center py-4">No leads added in this run</p>
                                      )}
                                    </div>
                                  </TabsContent>
                                  
                                  <TabsContent value="skipped" className="mt-3">
                                    <div className="max-h-[300px] overflow-y-auto space-y-2 pr-2">
                                      {activity.results.leads_skipped_details?.length > 0 ? (
                                        activity.results.leads_skipped_details.map((lead, idx) => (
                                          <div 
                                            key={idx} 
                                            className="p-3 rounded-lg bg-theme-darker/70 border border-theme-secondary/30 opacity-75"
                                          >
                                            <div className="flex items-start justify-between gap-2">
                                              <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-theme-light truncate">{lead.title}</p>
                                                <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-theme-muted">
                                                  <span className="flex items-center gap-1">
                                                    <Building2 className="w-3 h-3" />
                                                    {lead.client}
                                                  </span>
                                                  <Badge className="bg-theme-secondary/20 text-theme-secondary border-0 text-xs">
                                                    {lead.reason || "duplicate"}
                                                  </Badge>
                                                </div>
                                              </div>
                                              {lead.link && (
                                                <a 
                                                  href={lead.link} 
                                                  target="_blank" 
                                                  rel="noopener noreferrer"
                                                  className="text-theme-muted hover:text-theme-light transition-colors"
                                                >
                                                  <ExternalLink className="w-4 h-4" />
                                                </a>
                                              )}
                                            </div>
                                          </div>
                                        ))
                                      ) : (
                                        <p className="text-sm text-theme-muted text-center py-4">No leads skipped in this run</p>
                                      )}
                                    </div>
                                  </TabsContent>
                                </Tabs>
                              </div>
                            )}

                            {/* Detailed Logs Section - Link Verifier */}
                            {activity.agent_type === "link_verifier" && activity.results?.verification_details?.length > 0 && (
                              <div className="mb-4">
                                <div className="flex items-center gap-2 mb-3">
                                  <ShieldCheck className="w-4 h-4 text-theme-primary-light" />
                                  <h4 className="text-sm font-semibold text-theme-primary-light">Verification Details</h4>
                                </div>
                                <div className="max-h-[300px] overflow-y-auto space-y-2 pr-2">
                                  {activity.results.verification_details.map((item, idx) => (
                                    <div 
                                      key={idx} 
                                      className={`p-3 rounded-lg bg-theme-darker/70 border ${
                                        item.status === "valid" ? "border-theme-primary/30" :
                                        item.status === "invalid" || item.status === "timeout" ? "border-theme-danger/30" :
                                        item.status === "login_wall" ? "border-theme-secondary/30" :
                                        "border-[#a8c8c4]/30"
                                      }`}
                                    >
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium text-theme-light truncate">{item.title}</p>
                                          <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-theme-muted">
                                            <span className="flex items-center gap-1">
                                              <Building2 className="w-3 h-3" />
                                              {item.client}
                                            </span>
                                          </div>
                                          {item.reason && (
                                            <p className="text-xs text-theme-muted mt-1 truncate">{item.reason}</p>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <Badge className={`border-0 text-xs ${
                                            item.status === "valid" ? "bg-theme-primary/20 text-theme-primary" :
                                            item.status === "invalid" || item.status === "timeout" ? "bg-theme-danger/20 text-theme-danger" :
                                            item.status === "login_wall" ? "bg-theme-secondary/20 text-theme-secondary" :
                                            "bg-[#a8c8c4]/20 text-theme-muted"
                                          }`}>
                                            {item.status === "valid" && <ShieldCheck className="w-3 h-3 mr-1" />}
                                            {(item.status === "invalid" || item.status === "timeout") && <ShieldX className="w-3 h-3 mr-1" />}
                                            {item.status === "login_wall" && <ShieldAlert className="w-3 h-3 mr-1" />}
                                            {item.status}
                                          </Badge>
                                          {item.link && (
                                            <a 
                                              href={item.link} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              className="text-theme-primary-light hover:text-theme-light transition-colors"
                                            >
                                              <ExternalLink className="w-4 h-4" />
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Errors Section */}
                            {activity.results?.errors?.length > 0 && (
                              <div className="mb-4">
                                <div className="flex items-center gap-2 mb-3">
                                  <AlertTriangle className="w-4 h-4 text-theme-danger" />
                                  <h4 className="text-sm font-semibold text-theme-danger">Errors</h4>
                                </div>
                                <div className="max-h-[150px] overflow-y-auto space-y-1 pr-2">
                                  {activity.results.errors.map((error, idx) => (
                                    <p key={idx} className="text-xs text-theme-danger font-mono bg-theme-danger/10 p-2 rounded">
                                      {error}
                                    </p>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Timing Info */}
                            <div className="mt-4 pt-3 border-t border-theme-primary/20 flex flex-wrap gap-4 text-xs text-theme-muted">
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                <span>Started: {new Date(activity.started_at).toLocaleString()}</span>
                              </div>
                              {activity.completed_at && (
                                <div className="flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" />
                                  <span>Completed: {new Date(activity.completed_at).toLocaleString()}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-1">
                                <Hash className="w-3 h-3" />
                                <span className="font-mono">ID: {activity.id.slice(0, 8)}...</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}
